import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../services/api';
import {
  Box,
  Button,
  Typography,
  TextField,
  Autocomplete,
  Grid,
  Container,
  CircularProgress,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  Error as ErrorIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Phone as PhoneIcon,
  Flag as FlagIcon,
  Badge as BadgeIcon,
  Check as CheckIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { register, loginWithGoogle } from '../slices/authSlice';
import { useLanguage } from '../contexts/LanguageContext';
import { phoneZones, getPhoneZone } from '../utils/phoneZones';
import PaidIcon from '@mui/icons-material/Paid';
import goldColors from '../themes/goldColors';
import { alpha } from '@mui/material/styles';
// Esquema de validación - Actualizado para incluir phone_number y country
const validationSchema = Yup.object({
  firstName: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters'),
  lastName: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
  phone_number: Yup.string()
    .required('Phone number is required')
    .matches(/^[0-9]+$/, 'Phone number must contain only digits')
    .min(7, 'Phone number must be at least 7 digits')
    .max(15, 'Phone number cannot exceed 15 digits'),
  country: Yup.string()
    .required('Country is required'),
  terms: Yup.boolean()
    .oneOf([true], 'You must accept the terms and conditions')
});
// El autocompletado del navegador (nombre, correo, contraseña guardados)
// escribe directo en el <input> del DOM antes de que MUI/Formik se enteren
// de que el campo ya no está vacío. Mientras tanto, el label flotante de
// MUI se queda "adentro" del campo en vez de subir arriba, y queda
// superpuesto visualmente con el texto autocompletado (el efecto de
// "letras encimadas" reportado en el login y el registro). Forzar
// shrink:true en el label deja el texto siempre arriba sin depender de ese
// estado, y el override de -webkit-autofill evita el tinte amarillo de
// Chrome que agravaba el efecto.
const autofillSx = {
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px #fff inset',
    WebkitTextFillColor: '#000',
  },
};
const shrinkLabel = { shrink: true };
// CAPTCHA del registro (1 ago 2026, pedido de Augusto): Cloudflare
// Turnstile. La site key es pública (a diferencia de la secret key, que
// solo vive en el backend/Render) y se carga por variable de entorno de
// Netlify. Mientras Augusto no la configure, REACT_APP_TURNSTILE_SITE_KEY
// queda vacío, el widget simplemente no se muestra, y el backend tampoco
// exige el token (TURNSTILE_SECRET_KEY vacío ahí también) -- ambos lados
// se activan juntos el día que se cargan las dos llaves reales.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
// "Continuar con Google" (6 ago 2026, pedido de Augusto). Mismo patrón
// "inerte hasta configurar" que TURNSTILE_SITE_KEY arriba: mientras esta
// variable de entorno de Netlify quede vacía, el botón no se muestra, y el
// backend tampoco lo exige (GOOGLE_CLIENT_ID vacío en Render).
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
// Componente principal
const RegisterFormSinglePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [serverError, setServerError] = useState('');
  // Password requirement checks
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const { language, updateLanguageFromCountry } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  // --- CAPTCHA (Cloudflare Turnstile) ---
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    const renderWidget = () => {
      if (!window.turnstile || !turnstileContainerRef.current) return;
      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => {
          setCaptchaToken(token);
          setCaptchaError('');
        },
        'expired-callback': () => setCaptchaToken(''),
        'error-callback': () => setCaptchaToken(''),
      });
    };
    if (window.turnstile) {
      renderWidget();
      return undefined;
    }
    const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', renderWidget);
      return () => existingScript.removeEventListener('load', renderWidget);
    }
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = renderWidget;
    document.body.appendChild(script);
    return undefined;
  }, []);
  // --- "Continuar con Google" (6 ago 2026) ---
  const googleButtonRef = useRef(null);
  const [googleError, setGoogleError] = useState('');
  // '', 'loading' o 'slow' -- ver nota en handleGoogleCredential.
  const [googleStatus, setGoogleStatus] = useState('');
  // Mismo endpoint que el login (POST /auth/google): si el email de la
  // cuenta de Google ya coincide con una cuenta existente se vincula ahí
  // (ver auth.py), y si no, crea una cuenta nueva sin contraseña. En
  // cualquier caso el resultado es un registro/login exitoso, así que
  // reusamos el mismo destino post-registro (/onboarding/intro) que el
  // formulario normal más abajo.
  const handleGoogleCredential = async (response) => {
    setGoogleError('');
    setGoogleStatus('loading');
    // 7 ago 2026 (Augusto: "no me permite ingresar con Google... no pasa
    // nada"): reproduciéndolo, SÍ funcionaba -- el backend en Render
    // (plan gratis) estaba dormido y tardó ~15s en despertar, y este
    // flujo no mostraba ningún aviso mientras tanto (a diferencia del
    // registro con formulario normal, que si tarda igual no avisa, pero
    // al menos deshabilita el botón con un spinner -- ver más abajo). El
    // botón de Google se queda estático sin importar cuánto tarde, así
    // que parecía "no responder".
    const slowTimer = setTimeout(() => setGoogleStatus('slow'), 4000);
    try {
      const userData = await dispatch(loginWithGoogle(response.credential)).unwrap();
      const token = userData.access_token || userData.token;
      if (token) {
        sessionStorage.setItem('token', token);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('isNewUser', 'true');
      }
      localStorage.setItem('comingFromRegistration', 'true');
      // 10 ago 2026: mismo destino que el registro con formulario normal
      // -- ver comentario en el handler de abajo (OnboardingIntro.js).
      window.location.href = '/onboarding/intro';
    } catch (error) {
      console.error('Google sign-up failed:', error);
      setGoogleError(
        language === 'es'
          ? 'No se pudo continuar con Google. Intenta de nuevo.'
          : 'Google sign-up failed. Please try again.'
      );
      setGoogleStatus('');
    } finally {
      clearTimeout(slowTimer);
    }
  };
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;
    const renderButton = () => {
      if (!window.google?.accounts?.id || !googleButtonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: 328,
        text: 'signup_with',
        locale: language === 'es' ? 'es' : 'en',
      });
    };
    if (window.google?.accounts?.id) {
      renderButton();
      return undefined;
    }
    const existingScript = document.querySelector(`script[src="${GOOGLE_GSI_SCRIPT_SRC}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', renderButton);
      return () => existingScript.removeEventListener('load', renderButton);
    }
    const script = document.createElement('script');
    script.src = GOOGLE_GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = renderButton;
    document.body.appendChild(script);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);
  // Function to check password requirements
  const checkPasswordRequirements = (password) => {
    setPasswordRequirements({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password)
    });
  };

  // Toggle password visibility handlers
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  // Configuración de Formik
  // State for raw server error display
  const [rawServerError, setRawServerError] = useState(null);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone_number: '',
      country: '',
      countryObject: null,
      selectedCountryCode: '+1',
      terms: false
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // CAPTCHA: solo se exige si el widget está activo (site key
      // configurada) -- ver nota en TURNSTILE_SITE_KEY más arriba.
      if (TURNSTILE_SITE_KEY && !captchaToken) {
        setCaptchaError(
          language === 'es'
            ? 'Completa la verificación de seguridad para continuar.'
            : 'Please complete the security check to continue.'
        );
        return;
      }
      try {
        // Clear any previous errors and set submitting state
        setIsSubmitting(true);
        setServerError('');
        // El backend (schemas/user.py -> UserCreate) espera snake_case y no
        // tiene alias configurado, así que first_name/last_name son los
        // nombres reales de los campos. Antes se mandaba firstName/lastName
        // (camelCase) y Pydantic los ignoraba silenciosamente: el registro
        // "funcionaba" pero el nombre y apellido quedaban NULL en la BD.
        // 11 ago 2026 (bug real encontrado por Augusto: tuvo que agregar el
        // +51 a mano en su perfil porque el registro no lo había guardado):
        // el selector de país de abajo SÍ elige el código telefónico
        // correcto (selectedCountryCode, ej. "+51") y lo muestra como
        // prefijo en el campo de teléfono, pero acá se descartaba -- se
        // mandaba solo phone_number.replace(/\D/g, ''), puros dígitos sin
        // el código de país. El resultado: todo cliente nuevo quedaba con
        // un teléfono guardado sin código de país, inútil para matchear
        // contra el "From" que manda Twilio en el webhook de WhatsApp (ver
        // endpoints/whatsapp.py, _normalize_phone). Ahora se antepone el
        // código seleccionado.
        const registrationData = {
          email: values.email,
          first_name: values.firstName,
          last_name: values.lastName,
          password: values.password,
          phone_number: `${values.selectedCountryCode}${values.phone_number.replace(/\D/g, '')}`,
          captcha_token: captchaToken || undefined,
        };

        console.log('Submitting registration:', registrationData);
        // Usamos el cliente axios compartido (services/api.js), que ya
        // arma la URL correcta una sola vez (`${API_URL}/api/v1`). Antes
        // este formulario armaba su propia URL a mano con un
        // `/api/v1/auth/register` fijo — cuando API_URL en producción ya
        // incluía `/api/v1` (como está configurado en Netlify), el
        // resultado era `/api/v1/api/v1/auth/register`, un 404 real que
        // rompía el registro de cuentas nuevas en el sitio en vivo.
        let response;
        let responseData = {};
        try {
          response = await apiClient.post('/auth/register', registrationData);
          responseData = response.data || {};
        } catch (err) {
          if (err.response) {
            response = err.response;
            responseData = err.response.data || {};
          } else {
            throw err;
          }
        }
        const ok = response.status >= 200 && response.status < 300;
        console.log('Registration response:', response.status, responseData);
        if (ok) {
          // Registration successful!
          console.log('Registration successful! Redirecting to welcome page...');

          // Store ALL user data and authentication state in localStorage for access after redirect
          // Incluyendo todos los campos recopilados durante el registro
          const userRegData = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phone: values.phone_number,
            countryCode: values.selectedCountryCode,
            country: values.country || values.countryObject?.name || '',
            user_id: `${values.firstName.toLowerCase()}_${values.lastName.toLowerCase()}`.replace(/[^a-z0-9_]/g, '')
          };

          // Guardar todos los datos del registro
          localStorage.setItem('registeredUser', JSON.stringify(userRegData));
          console.log('Datos de registro guardados:', userRegData);

          // Ensure authentication state is set - CRITICAL for navigation to work
          const token = responseData.access_token || responseData.token;
          if (token) {
            // Guardar token explícitamente (3 ago 2026: en sessionStorage,
            // no localStorage -- ver services/api.js. Así cerrar el
            // navegador cierra también la sesión de este cliente nuevo).
            sessionStorage.setItem('token', token);
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('isNewUser', 'true');

            // Establecer token como encabezado predeterminado para futuras solicitudes
            if (window.axios) {
              window.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }

            console.log('Token guardado correctamente:', token);
          } else {
            console.error('No se encontró token en la respuesta:', responseData);
          }

          // Show success message briefly
          setShowSuccess(true);

          // 10 ago 2026 (Augusto: "no aparece la pantalla de bienvenida
          // que le indica lo que va a pasar y lo guía de la mano"): antes
          // esto mandaba directo a /profile, sin ningún contexto -- ahora
          // pasa primero por /onboarding/intro (ver OnboardingIntro.js),
          // que explica los 3 pasos (Perfil/Cuentas/Deudas) y cuánto va a
          // tomar, con un botón "Comenzar" que recién ahí lleva a /profile.
          console.log('FORCEFULLY redirecting to onboarding intro page');
          // Marcar que viene del registro para el flujo de redirección posterior
          localStorage.setItem('comingFromRegistration', 'true');
          // APPROACH 1: Set a very short timeout and use direct window.location
          // This is the most aggressive approach and should work in all browsers
          setTimeout(() => {
            console.log('Executing forced redirect to onboarding intro');
            window.location.href = '/onboarding/intro';
          }, 1000); // Longer delay to ensure state is properly set
          // APPROACH 2: Also try the navigate function as a backup
          try {
            navigate('/onboarding/intro');
          } catch (e) {
            console.log('Navigate approach failed, relying on window.location');
          }
          // APPROACH 3: Create and click a link element as another fallback
          try {
            const link = document.createElement('a');
            link.href = '/onboarding/intro';
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
          } catch (e) {
            console.log('Link approach failed, relying on other methods');
          }
        } else {
          // Handle specific error messages
          const errorMessage = responseData.detail || responseData.message || 'Registration failed';
          console.error('Registration failed:', errorMessage);
          // El token de Turnstile es de un solo uso -- si el registro
          // falló por cualquier motivo, hay que pedir uno nuevo antes de
          // reintentar.
          if (TURNSTILE_SITE_KEY && window.turnstile && turnstileWidgetIdRef.current !== null) {
            window.turnstile.reset(turnstileWidgetIdRef.current);
          }
          setCaptchaToken('');
          if (errorMessage.includes('Email already registered') || errorMessage.includes('already registered')) {
            setServerError('This email is already registered. Please use a different email or try logging in.');
          } else if (errorMessage.includes('phone')) {
            setServerError('This phone number is already registered or invalid. Please use a different phone number.');
          } else {
            setServerError(`Registration failed: ${errorMessage}`);
          }
        }

      } catch (error) {
        // Clear submitting state
        setIsSubmitting(false);

        console.error('Registration error:', error);

        // Simplified error handling for network errors
        if (error.message.includes('fetch')) {
          console.error('Network error:', error.message);
          setServerError(
            language === 'es'
              ? 'Error de conexión: No se puede conectar con el servidor. Por favor verifica tu conexión a internet.'
              : 'Network Error: Cannot connect to registration server. Please check your internet connection.');
          return;
        }

        // For all other errors, show a simple message
        setServerError(
          language === 'es'
            ? `Error de registro: ${error.message || 'Error desconocido'}. Por favor intenta de nuevo.`
            : `Registration failed: ${error.message || 'Unknown error'}. Please try again.`);

        // Show the raw error in console for debugging
        console.error('Full error details:', error);

        // Focus on the first field with an error
        if (formik.errors.email) {
          document.getElementById('email')?.focus();
        } else if (formik.errors.phone_number) {
          document.getElementById('phone_number')?.focus();
        } else if (formik.errors.password) {
          document.getElementById('password')?.focus();
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  });
  return (
    <Container maxWidth="md" sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 2
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: '800px',
        border: '1px solid #EAEAEA',
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: '#fff'
      }}>
        {/* Header */}
        {/* 3 ago 2026 (pedido de Augusto): mismo tratamiento visual que se
            hizo en /login -- ícono de marca dorado + degradé sutil arriba,
            para que el registro se sienta parte de la misma familia visual
            en vez de una plantilla genérica aparte. */}
        <Box sx={{
          p: 3,
          borderBottom: '1px solid #EAEAEA',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          backgroundImage: `linear-gradient(180deg, ${alpha(goldColors.main, 0.08)}, transparent 140px)`,
        }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: goldColors.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.5,
            }}
          >
            <PaidIcon sx={{ color: 'white', fontSize: 26 }} />
          </Box>
          <Typography variant="h4" fontWeight="600" gutterBottom sx={{ color: '#111827' }}>
            {language === 'es' ? 'Crear una cuenta' : 'Create an account'}
          </Typography>
          <Typography variant="body1" color="#6B7280">
            {language === 'es'
              ? 'Completa tu información para comenzar'
              : 'Fill in your information to get started'}
          </Typography>
        </Box>
        {/* "Continuar con Google" (6 ago 2026, movido arriba el 7 ago 2026
            -- Augusto reportó que en el formulario largo quedaba casi
            oculto al final, después de todos los campos manuales y el
            captcha. Como atajo rápido va arriba de todo, antes de pedir
            cualquier dato a mano. Solo se muestra si hay client ID
            configurado -- ver nota en GOOGLE_CLIENT_ID más arriba). */}
        {GOOGLE_CLIENT_ID && (
          <Box sx={{ px: 3, pt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box ref={googleButtonRef} />
            </Box>
            {/* Feedback mientras procesa (7 ago 2026, ver nota en
                handleGoogleCredential) -- antes no había nada acá y el
                botón se quedaba igual sin importar cuánto tardara el
                backend en responder. */}
            {googleStatus === 'loading' && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  {language === 'es' ? 'Conectando con Google...' : 'Connecting to Google...'}
                </Typography>
              </Box>
            )}
            {googleStatus === 'slow' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                {language === 'es'
                  ? 'El servidor estaba inactivo y está despertando, un momento...'
                  : 'The server was asleep and is waking up, one moment...'}
              </Typography>
            )}
            {googleError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                {googleError}
              </Typography>
            )}
            <Divider sx={{ mt: 3 }}>
              <Typography variant="caption" color="text.secondary">
                {language === 'es' ? 'o completa el formulario manualmente' : 'or fill in the form manually'}
              </Typography>
            </Divider>
          </Box>
        )}
        {/* Form */}
        <Box sx={{ p: 3 }}>
          <form onSubmit={formik.handleSubmit}>
            {showSuccess && (
              <Box sx={{
                mb: 3,
                p: 2,
                borderRadius: '4px',
                bgcolor: '#F0FDF4',
                border: '1px solid #D1FAE5',
                display: 'flex',
                alignItems: 'center'
              }}>
                <CheckIcon sx={{ color: '#10B981', mr: 1 }} />
                <Typography color="#047857">
                  {language === 'es'
                    ? '¡Registro exitoso! Redirigiendo...'
                    : 'Registration successful! Redirecting...'}
                </Typography>
              </Box>
            )}

            {formik.errors.general && (
              <Box sx={{
                mb: 3,
                p: 3,
                borderRadius: '8px',
                bgcolor: '#FEF2F2',
                border: '2px solid #EF4444',
                display: 'flex',
                alignItems: 'flex-start',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}>
                <ErrorIcon sx={{ color: '#EF4444', mr: 2, mt: 0.5, fontSize: '28px' }} />
                <Box sx={{ width: '100%' }}>
                  <Typography variant="h6" color="#B91C1C" fontWeight="600" gutterBottom>
                    {language === 'es' ? 'Error de Registro' : 'Registration Failed'}
                  </Typography>
                  <Typography color="#B91C1C" fontWeight="500" variant="body1" sx={{ fontSize: '1rem' }}>
                    {formik.errors.general}
                  </Typography>

                  {/* Raw server error display */}
                  {rawServerError && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: '#FECACA',
                        borderRadius: '4px',
                        border: '1px dashed #F87171',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: '200px',
                        overflow: 'auto'
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
                        Server Response Details:
                      </Typography>

                      {rawServerError.status && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          Status: {rawServerError.status} {rawServerError.statusText}
                        </Typography>
                      )}

                      {rawServerError.data && (
                        <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                          Data: {JSON.stringify(rawServerError.data, null, 2)}
                        </Typography>
                      )}

                      {rawServerError.message && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          Message: {rawServerError.message}
                        </Typography>
                      )}

                      {rawServerError.code && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          Code: {rawServerError.code}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Display server error message if present */}
            {serverError && (
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 1,
                  backgroundColor: '#FDEDED',
                  color: '#5F2120',
                  border: '1px solid #EF5350'
                }}
              >
                <Typography variant="body2">
                  <ErrorOutlineIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {serverError}
                </Typography>
              </Box>
            )}

            <Grid container spacing={2}>
              {/* Name fields */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="firstName"
                  name="firstName"
                  label={language === 'es' ? 'Nombre' : 'First name'}
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                  variant="outlined"
                  size="small"
                  autoComplete="given-name"
                  InputLabelProps={shrinkLabel}
                  sx={autofillSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="lastName"
                  name="lastName"
                  label={language === 'es' ? 'Apellido' : 'Last name'}
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  variant="outlined"
                  size="small"
                  autoComplete="family-name"
                  InputLabelProps={shrinkLabel}
                  sx={autofillSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  type="email"
                  label={language === 'es' ? 'Correo electrónico' : 'Email'}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  variant="outlined"
                  size="small"
                  autoComplete="email"
                  InputLabelProps={shrinkLabel}
                  sx={autofillSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>

              {/* Country Selection with Phone Code Integration - Now First */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  id="country"
                  options={phoneZones}
                  fullWidth
                  getOptionLabel={(option) => option.name || ''}
                  value={formik.values.countryObject || null}
                  autoHighlight
                  autoComplete
                  openOnFocus
                  blurOnSelect
                  filterOptions={(options, state) => {
                    // Custom filter for better suggestions
                    const inputValue = state.inputValue.toLowerCase().trim();
                    if (inputValue === '') return options;

                    return options.filter(option =>
                      option.name.toLowerCase().includes(inputValue) ||
                      option.code.toLowerCase().includes(inputValue)
                    );
                  }}
                  onKeyDown={(event) => {
                    // Auto-complete when Tab is pressed
                    if (event.key === 'Tab' && !event.shiftKey) {
                      const inputValue = event.target.value.toLowerCase().trim();
                      if (inputValue && inputValue.length > 1) {
                        // Find the first matching country
                        const matchingCountry = phoneZones.find(option =>
                          option.name.toLowerCase().includes(inputValue)
                        );

                        if (matchingCountry) {
                          // Prevent default tab behavior
                          event.preventDefault();

                          // Set the matched country
                          formik.setFieldValue('country', matchingCountry.name);
                          formik.setFieldValue('countryObject', matchingCountry);
                          formik.setFieldValue('selectedCountryCode', matchingCountry.code);
                          formik.setFieldTouched('country', true, false);

                          // Move focus to the next field
                          const phoneInput = document.getElementById('phone_number');
                          if (phoneInput) {
                            setTimeout(() => phoneInput.focus(), 50);
                          }
                        }
                      }
                    }
                  }}
                  onChange={(event, newValue) => {
                    // Update country value and selected code
                    if (newValue) {
                      formik.setFieldValue('country', newValue.name);
                      formik.setFieldValue('countryObject', newValue);
                      formik.setFieldValue('selectedCountryCode', newValue.code);
                      // Log for debugging
                      console.log(`Selected country: ${newValue.name} with phone code: ${newValue.code}`);

                      // Trigger field touch to ensure validation happens
                      formik.setFieldTouched('country', true, false);

                      // Force validation
                      setTimeout(() => {
                        formik.validateField('country');
                        formik.validateField('phone_number');
                      }, 100);
                    } else {
                      formik.setFieldValue('country', '');
                      formik.setFieldValue('countryObject', null);
                      formik.setFieldValue('selectedCountryCode', '+1');
                    }
                  }}
                  groupBy={(option) => {
                    // Group popular countries at the top
                    const popularCountries = ['United States', 'Canada', 'Mexico', 'United Kingdom', 'Spain', 'Colombia'];
                    return popularCountries.includes(option.name) ? 'Popular' : 'All Countries';
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        justifyContent: 'space-between',
                        py: 0.5,
                        borderBottom: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: 24,
                              fontSize: '1rem',
                              mr: 1,
                              textAlign: 'center'
                            }}
                          >
                            {option.name === 'United States' ? '🇺🇸' :
                             option.name === 'Canada' ? '🇨🇦' :
                             option.name === 'Mexico' ? '🇲🇽' :
                             option.name === 'United Kingdom' ? '🇬🇧' :
                             option.name === 'Spain' ? '🇪🇸' :
                             option.name === 'Colombia' ? '🇨🇴' : ''}
                          </Box>
                          <Typography>{option.name}</Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#4B5563',
                            fontWeight: 'medium',
                            bgcolor: '#F3F4F6',
                            px: 0.8,
                            py: 0.3,
                            borderRadius: 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          {option.code}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="country"
                      label={language === 'es' ? 'País' : 'Country'}
                      error={formik.touched.country && Boolean(formik.errors.country)}
                      helperText={
                        (formik.touched.country && formik.errors.country) ||
                        (language === 'es' ? 'Selecciona tu país para el código telefónico' : 'Select your country for phone code')
                      }
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                      sx={autofillSx}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <FlagIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 1 }
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Phone Number with Country Code - Now Second */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="phone_number"
                  name="phone_number"
                  type="tel"
                  label={language === 'es' ? 'Número de Teléfono' : 'Phone Number'}
                  placeholder="5551234567"
                  value={formik.values.phone_number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                  helperText={
                    (formik.touched.phone_number && formik.errors.phone_number) ||
                    (language === 'es' ? 'Solo números, sin espacios ni guiones' : 'Numbers only, no spaces or dashes')
                  }
                  variant="outlined"
                  size="small"
                  autoComplete="tel-national"
                  InputLabelProps={shrinkLabel}
                  sx={autofillSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon fontSize="small" sx={{ color: '#9CA3AF', mr: 0.5 }} />
                          <Box sx={{
                              color: '#4B5563',
                              fontWeight: 'medium',
                              backgroundColor: '#F3F4F6',
                              py: 0.5,
                              px: 1,
                              borderRadius: 1,
                              minWidth: '45px',
                              display: 'flex',
                              justifyContent: 'center'
                            }}
                          >
                            {/* Find selected country code or default to +1 */}
                            {formik.values.selectedCountryCode || '+1'}
                          </Box>
                        </Box>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>

              {/* Password fields */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  label={language === 'es' ? 'Contraseña' : 'Password'}
                  value={formik.values.password}
                  onChange={(e) => {
                    formik.handleChange(e);
                    const password = e.target.value;
                    // Show password requirements when user starts typing
                    if (password.length > 0) {
                      setShowPasswordRequirements(true);
                      // Check which requirements are met
                      checkPasswordRequirements(password);
                    } else {
                      setShowPasswordRequirements(false);
                      // Reset requirements when password is empty
                      setPasswordRequirements({
                        length: false,
                        uppercase: false,
                        lowercase: false,
                        number: false,
                        special: false
                      });
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  variant="outlined"
                  size="small"
                  autoComplete="new-password"
                  InputLabelProps={shrinkLabel}
                  sx={autofillSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1 }
                  }}
                />

                {/* Password Requirements */}
                {showPasswordRequirements && (
                  <Box sx={{
                    mt: 1,
                    p: 2,
                    borderRadius: '4px',
                    bgcolor: '#F3F4F6',
                    border: '1px solid #E5E7EB',
                    width: '100%'
                  }}>
                    <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                      {language === 'es' ? 'Requisitos de contraseña:' : 'Password Requirements:'}
                    </Typography>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '20px' }}>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {passwordRequirements.length ? (
                          <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: '1rem' }} />
                        ) : (
                          <ErrorIcon sx={{ color: 'text.disabled', mr: 1, fontSize: '1rem' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: passwordRequirements.length ? 'success.main' : 'text.secondary',
                            fontWeight: passwordRequirements.length ? 500 : 400
                          }}
                        >
                          {language === 'es' ? 'Mínimo 8 caracteres de longitud' : 'Minimum 8 characters long'}
                        </Typography>
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {passwordRequirements.uppercase ? (
                          <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: '1rem' }} />
                        ) : (
                          <ErrorIcon sx={{ color: 'text.disabled', mr: 1, fontSize: '1rem' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: passwordRequirements.uppercase ? 'success.main' : 'text.secondary',
                            fontWeight: passwordRequirements.uppercase ? 500 : 400
                          }}
                        >
                          {language === 'es' ? 'Al menos una letra mayúscula (A-Z)' : 'At least one uppercase letter (A-Z)'}
                        </Typography>
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {passwordRequirements.lowercase ? (
                          <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: '1rem' }} />
                        ) : (
                          <ErrorIcon sx={{ color: 'text.disabled', mr: 1, fontSize: '1rem' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: passwordRequirements.lowercase ? 'success.main' : 'text.secondary',
                            fontWeight: passwordRequirements.lowercase ? 500 : 400
                          }}
                        >
                          {language === 'es' ? 'Al menos una letra minúscula (a-z)' : 'At least one lowercase letter (a-z)'}
                        </Typography>
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {passwordRequirements.number ? (
                          <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: '1rem' }} />
                        ) : (
                          <ErrorIcon sx={{ color: 'text.disabled', mr: 1, fontSize: '1rem' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: passwordRequirements.number ? 'success.main' : 'text.secondary',
                            fontWeight: passwordRequirements.number ? 500 : 400
                          }}
                        >
                          {language === 'es' ? 'Al menos un número (0-9)' : 'At least one number (0-9)'}
                        </Typography>
                      </Box>
                      <Box component="li" sx={{ display: 'flex', alignItems: 'center' }}>
                        {passwordRequirements.special ? (
                          <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: '1rem' }} />
                        ) : (
                          <ErrorIcon sx={{ color: 'text.disabled', mr: 1, fontSize: '1rem' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: passwordRequirements.special ? 'success.main' : 'text.secondary',
                            fontWeight: passwordRequirements.special ? 500 : 400
                          }}
                        >
                          {language === 'es' ? 'Al menos un carácter especial (ej: !@#$%^&*)' : 'At least one special character (e.g., !@#$%^&*)'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  label={language === 'es' ? 'Confirmar contraseña' : 'Confirm password'}
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                  helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  variant="outlined"
                  size="small"
                  autoComplete="new-password"
                  InputLabelProps={shrinkLabel}
                  sx={autofillSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={handleToggleConfirmPasswordVisibility}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1 }
                  }}
                />
              </Grid>

              {/* CAPTCHA (Cloudflare Turnstile) -- solo se muestra si hay
                  site key configurada (Netlify env var). Ver nota en
                  TURNSTILE_SITE_KEY. */}
              {TURNSTILE_SITE_KEY && (
                <Grid item xs={12}>
                  <Box ref={turnstileContainerRef} />
                  {captchaError && (
                    <Typography variant="caption" color="error">
                      {captchaError}
                    </Typography>
                  )}
                </Grid>
              )}
              {/* Terms checkbox */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="terms"
                      name="terms"
                      checked={formik.values.terms}
                      onChange={formik.handleChange}
                      size="small"
                      sx={{
                        color: '#9CA3AF',
                        '&.Mui-checked': {
                          color: '#000',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="textSecondary">
                      {language === 'es'
                        ? 'Acepto los términos y condiciones'
                        : 'I agree to the terms and conditions'}
                    </Typography>
                  }
                />
                {formik.touched.terms && formik.errors.terms && (
                  <Typography variant="caption" color="error">
                    {formik.errors.terms}
                  </Typography>
                )}
              </Grid>
              {/* Submit button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    py: 1.5,
                    // 3 ago 2026: dorado de marca (goldColors.main), igual
                    // que el botón de /login -- antes era negro (#000),
                    // sin relación con el resto de la marca.
                    bgcolor: goldColors.main,
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 'medium',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: goldColors.dark,
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#e0e0e0',
                      color: '#9e9e9e',
                    }
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : language === 'es' ? 'Crear cuenta' : 'Create account'}
                </Button>
              </Grid>

              {/* Login link */}
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {language === 'es' ? '¿Ya tienes una cuenta?' : 'Already have an account?'}{' '}
                  <Button
                    sx={{
                      p: 0,
                      textTransform: 'none',
                      fontWeight: 'medium',
                      color: '#000',
                      '&:hover': {
                        bgcolor: 'transparent',
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={() => navigate('/login')}
                  >
                    {language === 'es' ? 'Inicia sesión' : 'Sign in'}
                  </Button>
                </Typography>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Box>
    </Container>
  );
};
export default RegisterFormSinglePage;